// frontend/src/components/Layout/PublicFooter.tsx
// Single standardized footer component for all public-facing layouts with atmyarea branding

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin
} from 'lucide-react';

interface PublicFooterProps {
  variant?: 'full' | 'minimal';
  className?: string;
}

const PublicFooter: React.FC<PublicFooterProps> = ({ 
  variant = 'full',
  className = '' 
}) => {
  const currentYear = new Date().getFullYear();

  // Minimal footer for auth pages, booking, etc.
  if (variant === 'minimal') {
    return (
      <footer className={`bg-gray-900 text-white py-6 ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">A</span>
              </div>
              <span className="text-lg font-bold">atmyarea</span>
            </div>
            <p className="text-gray-400 text-sm">
              © {currentYear} atmyarea. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  // Full footer for main pages
  const footerSections = {
    services: {
      title: 'Services',
      links: [
        { name: 'Browse All Services', href: '/services' },
        { name: 'Home Services', href: '/services/home' },
        { name: 'Business Services', href: '/services/business' },
        { name: 'Emergency Services', href: '/services/emergency' }
      ]
    },
    company: {
      title: 'Company',
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'How It Works', href: '/how-it-works' },
        { name: 'Careers', href: '/careers' },
        { name: 'Press & Media', href: '/press' }
      ]
    },
    support: {
      title: 'Support',
      links: [
        { name: 'Help Center', href: '/help' },
        { name: 'Contact Us', href: '/contact' },
        { name: 'Safety Center', href: '/safety' },
        { name: 'Trust & Safety', href: '/trust-safety' }
      ]
    },
    getStarted: {
      title: 'Get Started',
      links: [
        { name: 'Sign In', href: '/login' }, // ✅ FIXED
        { name: 'Join as Customer', href: '/register/customer' }, // ✅ FIXED
        { name: 'Become a Provider', href: '/register/partner' }, // ✅ FIXED
        { name: 'How It Works', href: '/how-it-works' }
      ]
    }
  };

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, href: 'https://facebook.com/atmyarea' },
    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/atmyarea' },
    { name: 'Instagram', icon: Instagram, href: 'https://instagram.com/atmyarea' },
    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/atmyarea' }
  ];

  return (
    <footer className={`bg-gray-900 text-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Main Footer Content */}
        <div className="py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            
            {/* Company Info - 2 columns on large screens */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">A</span>
                </div>
                <span className="text-xl font-bold">atmyarea</span>
              </div>
              
              <p className="text-gray-300 mb-6 leading-relaxed">
                Connect with trusted local service providers for all your needs. 
                From home repairs to business services, find verified professionals 
                in your area.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-300 text-sm">support@atmyarea.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-300 text-sm">1-800-ATMYAREA</span>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-300 text-sm">Available Nationwide</span>
                </div>
              </div>
            </div>

            {/* Footer Navigation - 4 columns */}
            {Object.entries(footerSections).map(([key, section]) => (
              <div key={key} className="lg:col-span-1">
                <h3 className="text-white font-semibold mb-4">{section.title}</h3>
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-gray-300 hover:text-white transition-colors text-sm"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm">Follow us:</span>
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Legal Links */}
            <div className="flex items-center space-x-6 text-sm">
              <Link 
                to="/privacy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
              <Link 
                to="/cookies" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Cookie Policy
              </Link>
            </div>

            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © {currentYear} atmyarea. All rights reserved.
            </div>
          </div>
        </div>

        {/* App Download Section - Optional */}
        <div className="border-t border-gray-800 py-6">
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-4">
              Get the atmyarea app for the best experience
            </p>
            <div className="flex justify-center space-x-4">
              <div className="flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-lg">
                <span className="text-gray-300 text-sm">Coming Soon on</span>
                <span className="text-white text-sm font-medium">App Store & Play Store</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;